
// LiveNowBanner.jsx - Animated banner for when you're streaming
import React, { useState, useEffect } from 'react';
import './LiveNowBanner.css';

const LiveNowBanner = () => {
  const [isLive, setIsLive] = useState(false);
  const [streamData, setStreamData] = useState(null);
  const [viewerCount, setViewerCount] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    // Check if stream is live
    checkStreamStatus();
    
    // Poll every 30 seconds for stream status
    const interval = setInterval(checkStreamStatus, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const checkStreamStatus = async () => {
    // TODO: Replace with actual YouTube API call or your backend endpoint
    // For now, using mock data - you can manually set this to test
    const mockStreamData = {
      isLive: false, // Set to true to test the banner
      title: "🔴 LIVE NOW: Frozzlers Friday Night Fights - Tournament #5",
      viewers: Math.floor(Math.random() * 200) + 50,
      streamUrl: "https://youtube.com/live/YOUR_LIVE_STREAM_ID",
      lobbyCode: "LIVE123",
      streamType: "tournament", // can be: tournament, casual, coding, challenge
      startedAt: new Date(Date.now() - 1800000) // 30 minutes ago
    };

    if (mockStreamData.isLive) {
      setIsLive(true);
      setStreamData(mockStreamData);
      setViewerCount(mockStreamData.viewers);
    }
  };

  // Manage body class for padding
  useEffect(() => {
    if (isLive) {
      document.body.classList.add('has-live-banner');
      if (isMinimized) {
        document.body.classList.add('banner-minimized');
      } else {
        document.body.classList.remove('banner-minimized');
      }
    } else {
      document.body.classList.remove('has-live-banner');
      document.body.classList.remove('banner-minimized');
    }

    return () => {
      document.body.classList.remove('has-live-banner');
      document.body.classList.remove('banner-minimized');
    };
  }, [isLive, isMinimized]);

  if (!isLive || !streamData) return null;

  const getStreamDuration = () => {
    const now = new Date();
    const started = new Date(streamData.startedAt);
    const diff = Math.floor((now - started) / 1000);
    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const getStreamIcon = () => {
    switch(streamData.streamType) {
      case 'tournament': return '🏆';
      case 'coding': return '💻';
      case 'challenge': return '⚔️';
      default: return '🎮';
    }
  };

  return (
    <>
      {/* Main Banner */}
      <div className={`live-now-banner ${isMinimized ? 'minimized' : ''}`}>
        <div className="live-now-container">
          {/* Animated background effects */}
          <div className="pulse-bg"></div>
          <div className="shimmer-effect"></div>
          
          {/* Left Section - Live Indicator */}
          <div className="live-indicator-section">
            <div className="live-dot-container">
              <span className="live-dot"></span>
              <span className="live-dot-ring"></span>
              <span className="live-dot-ring ring-2"></span>
            </div>
            <span className="live-text">LIVE NOW</span>
            <span className="viewer-count">
              <span className="viewer-icon">👁</span>
              {viewerCount.toLocaleString()}
            </span>
            <span className="stream-duration">
              {getStreamDuration()}
            </span>
          </div>

          {/* Center Section - Stream Info */}
          <div className="stream-info-section">
            <div className="stream-title-container">
              <span className="stream-icon">{getStreamIcon()}</span>
              <marquee className="stream-title" scrollamount="3">
                {streamData.title}
              </marquee>
            </div>
            {streamData.lobbyCode && (
              <div className="lobby-code-container">
                <span className="lobby-label">JOIN NOW:</span>
                <span className="lobby-code">{streamData.lobbyCode}</span>
                <button 
                  className="copy-code-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(streamData.lobbyCode);
                    // You could add a toast notification here
                  }}
                  title="Copy lobby code"
                >
                  📋
                </button>
              </div>
            )}
          </div>

          {/* Right Section - Actions */}
          <div className="action-section">
            <a 
              href={streamData.streamUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="watch-now-btn"
            >
              <span className="btn-icon">▶️</span>
              <span className="btn-text">WATCH NOW</span>
            </a>
            <button 
              className="minimize-btn"
              onClick={() => setIsMinimized(!isMinimized)}
              title={isMinimized ? "Expand" : "Minimize"}
            >
              {isMinimized ? '🔽' : '🔼'}
            </button>
          </div>
        </div>

        {/* Minimized View */}
        {isMinimized && (
          <div className="minimized-content">
            <span className="live-dot"></span>
            <span>LIVE</span>
            <span className="minimized-viewers">{viewerCount} watching</span>
          </div>
        )}
      </div>

      {/* Floating Widget (Alternative/Additional) */}
      <div className="live-floating-widget">
        <a href={streamData.streamUrl} target="_blank" rel="noopener noreferrer">
          <div className="widget-pulse"></div>
          <div className="widget-content">
            <span className="widget-live-dot"></span>
            <span className="widget-text">LIVE</span>
            <span className="widget-viewers">{viewerCount}</span>
          </div>
        </a>
      </div>
    </>
  );
};

export default LiveNowBanner;
